import React, { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  service: "",
  property_type: "",
  address: "",
  preferred_time: "",
  details: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.phone ||
      !form.email ||
      !form.service ||
      !form.details
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        property_type: form.property_type || null,
        address: form.address || null,
        preferred_time: form.preferred_time || null,
        details: form.details,
      });
      setSuccess(true);
      setForm(initial);
      toast.success("Quote request received. We'll be in touch soon.");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Could not send your request. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="quote-form-success"
        className="bg-white border border-slate-200 p-8 lg:p-10 text-center"
      >
        <CheckCircle2 className="h-12 w-12 mx-auto text-[#0284C7]" strokeWidth={1.5} />
        <h3 className="font-display text-2xl font-bold mt-4 text-[#0F172A]">
          Quote Request Received
        </h3>
        <p className="text-slate-600 mt-2">
          Thanks! A team member will reach out shortly with your estimate. For
          urgent issues, please call us directly.
        </p>
        <Button
          data-testid="quote-form-send-another-button"
          onClick={() => setSuccess(false)}
          className="mt-6 rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white"
        >
          Submit another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="bg-white border border-slate-200 p-6 lg:p-10 space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full Name" required>
          <Input
            data-testid="quote-name-input"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="John Doe"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </Field>
        <Field label="Phone" required>
          <Input
            data-testid="quote-phone-input"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(781) 555-0100"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Email" required>
          <Input
            data-testid="quote-email-input"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </Field>
        <Field label="Service Needed" required>
          <Select value={form.service} onValueChange={(v) => update("service", v)}>
            <SelectTrigger
              data-testid="quote-service-select"
              className="rounded-none border-slate-300 h-11 focus:ring-[#0284C7]"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem
                  key={s.slug}
                  value={s.title}
                  data-testid={`quote-service-option-${s.slug}`}
                >
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="Other" data-testid="quote-service-option-other">
                Other / Not sure
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Property Type">
          <Select
            value={form.property_type}
            onValueChange={(v) => update("property_type", v)}
          >
            <SelectTrigger
              data-testid="quote-property-type-select"
              className="rounded-none border-slate-300 h-11 focus:ring-[#0284C7]"
            >
              <SelectValue placeholder="Residential / Commercial" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Single-family home" data-testid="quote-property-option-single-family">
                Single-family home
              </SelectItem>
              <SelectItem value="Condo / Apartment" data-testid="quote-property-option-condo">
                Condo / Apartment
              </SelectItem>
              <SelectItem value="Multifamily" data-testid="quote-property-option-multifamily">
                Multifamily
              </SelectItem>
              <SelectItem value="Commercial" data-testid="quote-property-option-commercial">
                Commercial
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred Time">
          <Input
            data-testid="quote-preferred-time-input"
            value={form.preferred_time}
            onChange={(e) => update("preferred_time", e.target.value)}
            placeholder="Morning, afternoon, ASAP..."
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
          />
        </Field>
      </div>

      <Field label="Service Address">
        <Input
          data-testid="quote-address-input"
          value={form.address}
          onChange={(e) => update("address", e.target.value)}
          placeholder="Street, City, MA"
          className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
        />
      </Field>

      <Field label="Project Details" required>
        <Textarea
          data-testid="quote-details-input"
          value={form.details}
          onChange={(e) => update("details", e.target.value)}
          placeholder="Describe the issue or project. The more we know, the better the estimate."
          rows={5}
          className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] resize-none"
          required
        />
      </Field>

      <Button
        type="submit"
        data-testid="quote-form-submit-button"
        disabled={loading}
        className="w-full rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold text-base py-6"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" strokeWidth={1.75} />
            Request Free Quote
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        Free, no-obligation estimate. We respond promptly during business hours.
      </p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </Label>
      {children}
    </div>
  );
}
